import { zodResolver } from "@hookform/resolvers/zod";
import { Button, IconButton } from "@hortis/ui/button";
import { DatePicker, createDateLabel } from "@hortis/ui/date-picker";
import {
  Dialog,
  DialogClose,
  DialogContent,
  DialogFooter,
  DialogTitle,
  DialogTrigger,
} from "@hortis/ui/dialog";
import {
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from "@hortis/ui/form";
import { Calendar, XClose } from "@hortis/ui/icons";
import { Input } from "@hortis/ui/input";
import { Textarea } from "@hortis/ui/textarea";
import { type ReactNode, useEffect, useState } from "react";
import { FormProvider, useForm } from "react-hook-form";
import { stopEventPropagation } from "src/features/collection/components/stop-event-propagation";
import { twMerge } from "tailwind-merge";
import { MaterialImageSchema } from "../add-plant-material-form/material-image-schema";
import type { StagedImage } from "./add-material-photos-button";
import { DiscardDialog } from "./discard-dialog";

export const PhotoDialog = ({
  initialValue,
  children,
  onSubmit,
}: {
  initialValue: StagedImage;
  children: ReactNode;
  onSubmit: (data: MaterialImageSchema) => void;
}) => {
  const [open, setOpen] = useState(false);
  const [discardDialogOpen, setDiscardDialogOpen] = useState(false);
  const form = useForm<MaterialImageSchema>({
    defaultValues: initialValue,
    resolver: zodResolver(MaterialImageSchema.omit({ assetUrl: true })),
  });

  useEffect(() => {
    if (open) {
      form.reset(initialValue);
    }
  }, [initialValue, form, open]);

  const handleSubmit = (data: MaterialImageSchema) => {
    onSubmit(data);
    setOpen(false);
  };

  const onOpenChange = (open: boolean) => {
    if (
      !open &&
      !discardDialogOpen &&
      Object.keys(form.formState.dirtyFields).length > 0
    ) {
      setDiscardDialogOpen(true);
    } else {
      setOpen(open);
      setDiscardDialogOpen(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogTrigger asChild onFocusCapture={stopEventPropagation}>
        {children}
      </DialogTrigger>
      <FormProvider {...form}>
        <DialogContent
          className="flex max-w-md gap-0! overflow-hidden p-0!"
          asChild
        >
          <form
            onSubmit={(e) => {
              e.stopPropagation();
              void form.handleSubmit(handleSubmit)(e);
            }}
            className="flex flex-col"
          >
            <DialogTitle className="flex items-center justify-between gap-2 border-b border-grey-200 px-4 py-2 lg:px-6">
              Edit photo
              <DialogClose asChild>
                <IconButton
                  icon={XClose}
                  size="sm"
                  ariaLabel="Close"
                  variant="tertiaryGray"
                />
              </DialogClose>
            </DialogTitle>
            <div className="flex flex-col gap-6 overflow-y-auto px-4 py-6 lg:px-6">
              <div className="relative flex h-52 min-h-52 w-full items-center justify-center overflow-hidden rounded-lg bg-grey-900">
                <img
                  alt="Preview"
                  src={initialValue.thumb}
                  className="absolute left-0 top-0 size-full rounded-lg object-fill"
                />
                <img
                  alt="Preview"
                  src={initialValue.thumb}
                  className="flex size-full max-h-full items-center justify-center overflow-hidden rounded-lg bg-grey-900/70 object-contain backdrop-blur-lg"
                />
              </div>
              <FormField
                control={form.control}
                name="capturedBy"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Captured by</FormLabel>
                    <FormControl>
                      <Input
                        value={field.value ?? undefined}
                        onChange={field.onChange}
                        data-cy="captured-by-input"
                      />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
              <FormField
                control={form.control}
                name="capturedAt"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Date captured</FormLabel>
                    <FormControl>
                      <DatePicker
                        value={field.value}
                        onChange={field.onChange}
                        data-cy="date-captured-picker"
                      >
                        <Button
                          endIcon={Calendar}
                          className={twMerge(
                            "justify-between",
                            field.value == null && "text-grey-500",
                          )}
                        >
                          {field.value == null
                            ? "Set date captured"
                            : createDateLabel(field.value)}
                        </Button>
                      </DatePicker>
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
              <FormField
                control={form.control}
                name="description"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Description</FormLabel>
                    <FormControl>
                      <Textarea
                        value={field.value ?? undefined}
                        onChange={field.onChange}
                        data-cy="photo-description-input"
                      />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
            </div>
            <DialogFooter className="border-t border-grey-200 px-4 py-3 lg:px-6">
              <DiscardDialog
                onDiscard={() => {
                  onOpenChange(false);
                }}
                open={discardDialogOpen}
                onOpenChange={setDiscardDialogOpen}
              >
                <DialogTrigger
                  asChild
                  onClick={(e) => {
                    e.stopPropagation();
                    e.preventDefault();
                    onOpenChange(false);
                  }}
                >
                  <Button
                    variant="tertiaryGray"
                    testId="cancel-edit-photo-button"
                  >
                    Cancel
                  </Button>
                </DialogTrigger>
              </DiscardDialog>
              <Button
                variant="primary"
                type="submit"
                testId="save-photo-button"
              >
                Save photo
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </FormProvider>
    </Dialog>
  );
};
